import { useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { QuoteItemDTO } from '@setvi/shared/services/react-query';
import { QuotesV2QueryKey } from 'Services/Query/QuotesV2';
import type { QuoteV2 } from 'Services/Query/QuotesV2/Types';

/**
 * Centralized cache operations for quote data and items.
 *
 * Quote details cache: stores QuoteV2 (no items)
 * Items cache: stores QuoteItemDTO[] (nested format, transformed from v2 flat)
 */
export const useQuoteCache = (quoteId: string) => {
  const queryClient = useQueryClient();

  const quoteQueryKey = useMemo(
    () => [QuotesV2QueryKey.GET_QUOTE, quoteId] as const,
    [quoteId]
  );

  const itemsQueryKey = useMemo(
    () => [QuotesV2QueryKey.GET_ITEMS_METADATA, quoteId] as const,
    [quoteId]
  );

  return useMemo(
    () => ({
      quoteQueryKey,
      itemsQueryKey,

      // ---- Quote details cache operations ----

      getQuote: () => queryClient.getQueryData<QuoteV2>(quoteQueryKey),

      setQuote: (updater: (old: QuoteV2 | undefined) => QuoteV2 | undefined) =>
        queryClient.setQueryData<QuoteV2>(quoteQueryKey, updater),

      cancelQuote: () => queryClient.cancelQueries({ queryKey: quoteQueryKey }),

      invalidateQuote: () =>
        queryClient.invalidateQueries({
          queryKey: quoteQueryKey,
          refetchType: 'none'
        }),

      refetchQuote: () =>
        queryClient.refetchQueries({ queryKey: quoteQueryKey }),

      // ---- Items cache operations ----

      getItems: () => queryClient.getQueryData<QuoteItemDTO[]>(itemsQueryKey),

      setItems: (
        updater: (old: QuoteItemDTO[] | undefined) => QuoteItemDTO[] | undefined
      ) => queryClient.setQueryData<QuoteItemDTO[]>(itemsQueryKey, updater),

      cancelItems: () => queryClient.cancelQueries({ queryKey: itemsQueryKey }),

      invalidateItems: () =>
        queryClient.invalidateQueries({
          queryKey: itemsQueryKey,
          refetchType: 'none'
        }),

      refetchItems: () =>
        queryClient.refetchQueries({ queryKey: itemsQueryKey }),

      // ---- Combined operations ----

      /** Cancel both quote and items queries */
      cancel: () =>
        Promise.all([
          queryClient.cancelQueries({ queryKey: quoteQueryKey }),
          queryClient.cancelQueries({ queryKey: itemsQueryKey })
        ]),

      /** Invalidate both quote and items */
      invalidate: () =>
        Promise.all([
          queryClient.invalidateQueries({
            queryKey: quoteQueryKey,
            refetchType: 'none'
          }),
          queryClient.invalidateQueries({
            queryKey: itemsQueryKey,
            refetchType: 'none'
          })
        ]),

      /** Refetch quote and items metadata */
      refetch: () =>
        Promise.all([
          queryClient.refetchQueries({ queryKey: quoteQueryKey }),
          queryClient.refetchQueries({ queryKey: itemsQueryKey })
        ]),

      queryClient
    }),
    [queryClient, quoteQueryKey, itemsQueryKey]
  );
};
